// The save flow of group create / plan change / identity edit (SPEC §6.1, §6.5): conflicts offer
// "Save anyway"; orphaned lesson changes offer "Delete them and save". Nothing is dropped silently: a confirmation
// only goes out for the form as it was when the problems were shown.

import type { ApiResult } from "../../api/client";
import type { LessonKey, Problem } from "../../api/types";
import { formatDate, t } from "../../i18n/i18n";
import { buildConflictList } from "../../ui/conflictList";
import { createButton, createElement } from "../../ui/dom";
import { describeProblem } from "../../ui/feedback";
import { describeFieldErrors } from "../../ui/forms";

export type SaveAttempt<T> = (confirm: boolean, confirmOrphans: boolean) => Promise<ApiResult<T>>;

/**
 * Runs the attempt; on success calls onSaved, otherwise fills the message box with what to do next. The box must sit
 * inside the form whose inputs the attempt reads.
 */
export async function runGroupSave<T>(attempt: SaveAttempt<T>, box: HTMLElement,
  onSaved: (data: T) => void): Promise<void> {
  await tryAgain(attempt, { confirm: false, confirmOrphans: false }, box, onSaved);
}

interface Confirmations {
  confirm: boolean;
  confirmOrphans: boolean;
}

async function tryAgain<T>(attempt: SaveAttempt<T>, confirmations: Confirmations, box: HTMLElement,
  onSaved: (data: T) => void): Promise<void> {
  const result: ApiResult<T> = await attempt(confirmations.confirm, confirmations.confirmOrphans);
  box.replaceChildren();
  if (result.ok) {
    onSaved(result.data);
    return;
  }

  const shownState: string = readFormState(box.parentElement);
  const retry = (next: Confirmations): Promise<void> => {
    // Edited since the problems were shown: confirming would skip problems nobody saw, so save plainly again.
    const isUnchanged: boolean = readFormState(box.parentElement) === shownState;
    return tryAgain(attempt, isUnchanged ? next : { confirm: false, confirmOrphans: false }, box, onSaved);
  };
  box.append(buildProblemView(result.problem, confirmations, retry));
}

/** Every input value of the form, in order (adding or removing a slot changes it too). */
function readFormState(form: HTMLElement | null): string {
  const controls: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | [] = form === null
    ? [] : form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input, select, textarea");
  return JSON.stringify([...controls].map((control): string => control.value));
}

function buildProblemView(problem: Problem, confirmations: Confirmations,
  retry: (next: Confirmations) => Promise<void>): HTMLElement {
  if (problem.code === "SCHEDULE_CONFLICTS") {
    return buildConflictList(problem.conflicts, (): Promise<void> => retry({ ...confirmations, confirm: true }));
  }

  if (problem.code === "ORPHANED_LESSON_CHANGES") {
    return buildOrphanList(problem, (): Promise<void> => retry({ ...confirmations, confirmOrphans: true }));
  }

  const message: string = problem.fieldErrors.length > 0
    ? `${describeProblem(problem)} ${describeFieldErrors(problem.fieldErrors)}`
    : describeProblem(problem);
  const box: HTMLDivElement = createElement("div", "error-box", message);
  box.setAttribute("role", "alert");
  return box;
}

function buildOrphanList(problem: Problem, onConfirm: () => Promise<void>): HTMLElement {
  const box: HTMLDivElement = createElement("div", "conflict-box");
  box.setAttribute("role", "alert");
  const list: HTMLUListElement = createElement("ul");
  const lessons: unknown = problem.params.lessons;
  for (const lesson of Array.isArray(lessons) ? (lessons as LessonKey[]) : []) {
    list.append(createElement("li", "", formatDate(lesson.date)));
  }

  box.append(createElement("p", "", t("groups.orphansTitle")), list, createButton(t("groups.deleteOrphans"),
    onConfirm, "button-warning"));
  return box;
}
